import {
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import { APPLICATIONS, ApplicationEnum } from "@repo/utils";
import { TemplateInbasketEnum } from "../../../convex/schema";
import {
  basculerApplication,
  LIBELLES_VARIANTE_INBASKET,
  type ChoixComposition,
} from "./compositionCampagne";
import type { ScorecardOption } from "./scorecardsCampagne";
import { SelecteurVersionScorecard } from "./SelecteurVersionScorecard";

/**
 * Composition d'une campagne : une case par application autorisée, dans
 * l'ordre du catalogue (l'ordre de passage). Sous une case cochée, le choix
 * qu'elle demande : variante de l'Inbasket, scorecard et version de
 * l'entretien structuré.
 */
export function ChoixEpreuves({
  autorisees,
  choix,
  onChange,
  scorecards,
}: {
  autorisees: readonly ApplicationEnum[];
  choix: ChoixComposition;
  onChange: (choix: ChoixComposition) => void;
  /** Scorecards de l'org ; `undefined` pendant le chargement. */
  scorecards: ScorecardOption[] | undefined;
}) {
  const options = APPLICATIONS.filter((a) => autorisees.includes(a.cle));

  return (
    <fieldset className="grid gap-2">
      <legend className="mb-2 text-sm font-medium">Épreuves</legend>
      {options.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Aucune application n&apos;est ouverte à votre organisation.
          Contactez votre administrateur.
        </p>
      ) : (
        <ul className="divide-y divide-charte-line overflow-hidden rounded-surface border border-charte-line">
          {options.map((a) => {
            const id = `epreuve-${a.cle}`;
            const cochee = choix.cochees.includes(a.cle);
            return (
              <li key={a.cle} className="grid gap-3 px-4 py-2.5">
                <div className="flex items-center gap-3">
                  {/* `htmlFor` plutôt qu'un <label> englobant : la Checkbox du
                      design system est un bouton, pas un input natif. */}
                  <Checkbox
                    id={id}
                    aria-describedby={`${id}-description`}
                    checked={cochee}
                    onCheckedChange={(v) =>
                      onChange(
                        basculerApplication(choix, a.cle, v === true, scorecards),
                      )
                    }
                  />
                  {/* La description reste hors du <label> : le nom de la case
                      est celui de l'application, la description la complète. */}
                  <div className="flex flex-1 flex-wrap items-baseline gap-x-1.5">
                    <label htmlFor={id} className="cursor-pointer text-sm">
                      {a.libelle}
                    </label>
                    <span
                      id={`${id}-description`}
                      className="text-sm text-muted-foreground"
                    >
                      ({a.description})
                    </span>
                  </div>
                </div>
                {cochee && a.cle === ApplicationEnum.INBASKET && (
                  <ChampVarianteInbasket choix={choix} onChange={onChange} />
                )}
                {cochee && a.cle === ApplicationEnum.ENT_STRUCT && (
                  <div className="grid gap-1 pl-7">
                    <Label htmlFor="epreuve-ent-struct-scorecard" className="text-xs">
                      Scorecard
                    </Label>
                    <SelecteurVersionScorecard
                      id="epreuve-ent-struct-scorecard"
                      scorecards={scorecards}
                      versionId={choix.entretien?.versionId}
                      onChange={(slug, versionId) =>
                        onChange({ ...choix, entretien: { slug, versionId } })
                      }
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </fieldset>
  );
}

function ChampVarianteInbasket({
  choix,
  onChange,
}: {
  choix: ChoixComposition;
  onChange: (choix: ChoixComposition) => void;
}) {
  return (
    <div className="grid gap-1 pl-7">
      <Label htmlFor="epreuve-inbasket-variante" className="text-xs">
        Variante
      </Label>
      <Select
        value={choix.varianteInbasket}
        onValueChange={(v) =>
          onChange({ ...choix, varianteInbasket: v as TemplateInbasketEnum })
        }
      >
        <SelectTrigger id="epreuve-inbasket-variante">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.values(TemplateInbasketEnum).map((t) => (
            <SelectItem key={t} value={t}>
              {LIBELLES_VARIANTE_INBASKET[t]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
